import { supabase } from '../supabaseClient';

export const objectivesService = {
  async getObjectivesData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get main objectives data (don't throw error if not found)
      const { data: objectivesData, error: objectivesDataError } = await supabase
        .from('objectives_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (objectivesDataError && objectivesDataError.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is okay
      }

      // Get objectives
      const { data: objectives } = await supabase
        .from('objectives')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      return {
        objectivesHeader: {
          title: objectivesData?.objectives_header_title || 'Nos Objectifs',
          subtitle: objectivesData?.objectives_header_subtitle || ''
        },
        objectives: (objectives || []).map(objective => ({
          id: objective.id,
          title: objective.title,
          description: objective.description,
          icon: objective.icon,
          displayOrder: objective.display_order
        }))
      };
    } catch (error) {
      throw error;
    }
  },

  async updateObjectivesPageData(objectivesData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        objectives_header_title: objectivesData.objectivesHeader?.title || 'Nos Objectifs',
        objectives_header_subtitle: objectivesData.objectivesHeader?.subtitle || '',
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('objectives_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('objectives_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('objectives_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      throw error;
    }
  },

  async addObjective(objectiveData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        title: objectiveData.title,
        description: objectiveData.description,
        icon: objectiveData.icon || 'HiOutlineLightBulb',
        display_order: objectiveData.displayOrder || 0
      };

      const { data, error } = await supabase
        .from('objectives')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateObjective(objectiveId, objectiveData) {
    try {
      const updateData = {
        title: objectiveData.title,
        description: objectiveData.description,
        icon: objectiveData.icon || 'HiOutlineLightBulb',
        display_order: objectiveData.displayOrder || 0,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('objectives')
        .update(updateData)
        .eq('id', objectiveId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteObjective(objectiveId) {
    try {
      const { error } = await supabase
        .from('objectives')
        .delete()
        .eq('id', objectiveId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
